import { useEffect, useCallback } from 'react';
import { Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { FindOrganizationDto } from '@evimo/types/api/organization/find-organization/find-organization.dto';
import type { CreateOrganizationRequestDto } from '@evimo/types/api/organization/create-organization/create-organization.request.dto';
import type { CreateOrganizationResponseDto } from '@evimo/types/api/organization/create-organization/create-organization.response.dto';
import type { UpdateOrganizationRequestDto } from '@evimo/types/api/organization/update-organization/update-organization.request.dto';
import type { UpdateOrganizationResponseDto } from '@evimo/types/api/organization/update-organization/update-organization.response.dto';
import { OrganizationDialogForm } from './OrganizationDialogForm';
import { useOrganizationMutation } from '~/app/hooks/organization/useOrganizationMutation';
import { OrganizationService } from '~/app/services/organization/organization.service';

type OrganizationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organization: FindOrganizationDto | null;
  existingNames: string[];
  parentOptions?: { value: string; label: string }[];
};

export function OrganizationDialog({
  open,
  onOpenChange,
  organization,
  existingNames,
  parentOptions,
}: OrganizationDialogProps) {
  const { t } = useTranslation('organization');
  const isEditMode = organization !== null;

  const createMutation = useOrganizationMutation<
    CreateOrganizationRequestDto,
    CreateOrganizationResponseDto
  >((dto) => OrganizationService.createOrganization(dto), {
    onSuccess: () => {
      message.success(
        t('messages.createOrganizationSuccess', 'Organization created!'),
      );
    },
    onError: () => {
      message.error(
        t(
          'messages.createOrganizationFailed',
          'Failed to create organization!',
        ),
      );
    },
  });

  const updateMutation = useOrganizationMutation<
    UpdateOrganizationRequestDto & { orgId: string },
    UpdateOrganizationResponseDto
  >(({ orgId, ...dto }) => OrganizationService.updateOrganization(orgId, dto), {
    onSuccess: () => {
      message.success(
        t('messages.updateOrganizationSuccess', 'Organization updated!'),
      );
    },
    onError: () => {
      message.error(
        t(
          'messages.updateOrganizationFailed',
          'Failed to update organization!',
        ),
      );
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }
  }, [open]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleOrgSaved = useCallback(
    async (
      dto: CreateOrganizationRequestDto,
    ): Promise<CreateOrganizationResponseDto> => {
      if (isEditMode && organization) {
        const updateResult = await updateMutation.mutateAsync({
          orgId: organization.id,
          name: dto.name,
          organizationType: dto.organizationType,
          mainLanguage: dto.mainLanguage,
          active: dto.active,
          parentId: dto.parentId,
          externalCsmsConfig: dto.externalCsmsConfig,
        });
        handleClose();
        return updateResult as CreateOrganizationResponseDto;
      }

      const result = await createMutation.mutateAsync(dto);
      handleClose();
      return result;
    },
    [isEditMode, organization, createMutation, updateMutation, handleClose],
  );

  return (
    <Modal
      open={open}
      title={isEditMode ? t('dialog.editTitle') : t('dialog.createTitle')}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={520}
    >
      <OrganizationDialogForm
        organization={organization}
        existingNames={existingNames}
        parentOptions={parentOptions}
        onSubmit={handleOrgSaved}
        onCancel={handleClose}
      />
    </Modal>
  );
}
